import { Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import {
  TDemographicsInsight,
  TLocation,
  TPosition,
  TReason,
  TRejectionReason,
  TSalaryPeriod,
} from '../../../../../interfaces/jobpost.interface';
import FlexibleSingleSelect from './FlexibleSingleSelect';

type ISelectItems = (
  | TLocation
  | TPosition
  | TReason
  | TSalaryPeriod
  | TRejectionReason
  | TDemographicsInsight
)[];

type TLabel =
  | 'position'
  | 'location'
  | 'reason'
  | 'salaryPeriod'
  | 'rejectionReason'
  | 'demographicsInsight';

const capitalizeAndAddColon = (str: TLabel) => {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }

  if (str === 'demographicsInsight') {
    return 'Choose type of statistics';
  }

  if (str === 'position') {
    return 'Employment Type';
  }

  if (str === 'location') {
    return 'Work Type';
  }

  if (str === 'salaryPeriod') {
    return 'Salary Period';
  }

  return str.charAt(0).toUpperCase() + str.slice(1) + ':';
};

interface JobPostFilterSingleSelectProps<T extends FieldValues = FieldValues> {
  control?: Control<T, any>;
  validationRules?: RegisterOptions<T>;
  name?: Path<T>;
  label: TLabel;
  selectItems: ISelectItems;
  placeholder: string;
  value?: string;
  onChange?: React.Dispatch<React.SetStateAction<string>>;
  helperText?: string;
  error?: boolean;
}

const JobPostFilterSingleSelect = <T extends FieldValues = FieldValues>({
  name,
  control,
  validationRules,
  selectItems,
  label,
  placeholder,
  value,
  onChange,
  helperText,
  error,
}: JobPostFilterSingleSelectProps<T>) => {
  const handleChange = (value: string) => {
    if (value) {
      onChange?.(value);
    }
  };

  if (control && !name) {
    throw Error('name prop is required when use form control');
  }

  return (
    <div>
      <Typography
        component={'p'}
        fontSize={'12px'}
        fontWeight={700}
        mb={'12px'}
      >
        {capitalizeAndAddColon(label)}
      </Typography>
      <FormControl error={error} sx={{ width: '100%' }}>
        {/* stand alone */}
        {!control && (
          <FlexibleSingleSelect
            label={label}
            value={value}
            onChange={(value) => handleChange(value)}
            selectItems={selectItems}
            placeholder={placeholder}
            helperText={helperText}
          />
        )}

        {/* part of form */}
        {control && name && (
          <Controller
            name={name}
            control={control}
            rules={validationRules}
            render={({ field }) => (
              <FlexibleSingleSelect
                label={label}
                field={field}
                selectItems={selectItems}
                placeholder={placeholder}
                helperText={helperText}
              />
            )}
          />
        )}
      </FormControl>
    </div>
  );
};

export default JobPostFilterSingleSelect;
